import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { usePeriod } from '@/lib/period';
import { PeriodControl } from '@/features/shared/PeriodControl';
import { AllTimePrintedCard } from './AllTimePrintedCard';
import { DistributionChart } from './DistributionChart';
import { TopTenantsTable } from './TopTenantsTable';
import { UsageHeatmap } from './UsageHeatmap';
import { useUsage } from './useUsage';

/**
 * Tela `/dashboard/usage`: total acumulado de etiquetas, heatmap por dia,
 * distribuição de tenants por faixa de uso e top 20 contas no período.
 *
 * Origem: sem issue — commit b7ad9714a, 2026-05-04 (Sprint 4.9; card do
 * acumulado em ffde3b389, 2026-08-31).
 *
 * Contrato: sem props; período pelo `usePeriod`. O card do acumulado
 * (`AllTimePrintedCard`) tem estado próprio e ignora o período; o resto
 * segue os estados LOADING/ERROR/READY do `useUsage`.
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/usage`, só super-admin).
 *
 * Arquitetura: só apresentação, sem escrita; leituras no `useUsage` e no
 * `useAllTimePrintedGlobal`. Spec: `docs/screens/dashboard-usage.md`. Os
 * números são o contador A (billing/BI, bucket UTC), não o do Monitor do
 * cliente — ver `docs/label-counters.md`.
 *
 * Issues: #297/#298/#299 (total acumulado).
 */
export function UsageDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { period, setPeriod } = usePeriod();
  const state = useUsage(period);

  return (
    <ContentLayout
      header={<Header variant="h1" description={t('usage.subtitle')}>{t('usage.title')}</Header>}
    >
      <SpaceBetween size="l">
        {/* Acima do PeriodControl: este número é all-time e não obedece o filtro. */}
        <AllTimePrintedCard />
        <PeriodControl value={period} onChange={setPeriod} />

        {state.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
        {state.kind === 'READY' && (
          <SpaceBetween size="l">
            <UsageHeatmap cells={state.heatCells} />
            <ColumnLayout columns={2}>
              <DistributionChart distribution={state.distribution} />
              <TopTenantsTable rows={state.topTenants} />
            </ColumnLayout>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}
